/**
 * Round a time value (in minutes past midnight) to the nearest interval.
 */
function roundTime(totalMinutes, intervalMin, direction) {
  if (!intervalMin || intervalMin <= 0) return totalMinutes;
  switch (direction) {
    case 'up':      return Math.ceil(totalMinutes  / intervalMin) * intervalMin;
    case 'down':    return Math.floor(totalMinutes / intervalMin) * intervalMin;
    case 'nearest': return Math.round(totalMinutes / intervalMin) * intervalMin;
    default:        return totalMinutes;
  }
}

/**
 * Parse a "HH:MM" time string into minutes past midnight.
 */
function timeStrToMinutes(str) {
  if (!str) return null;
  const [h, m] = str.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Set hours/minutes on a Date object (local time), returning a new Date.
 */
function setTimeOnDate(date, minutesPastMidnight) {
  const d = new Date(date);
  d.setHours(Math.floor(minutesPastMidnight / 60), minutesPastMidnight % 60, 0, 0);
  return d;
}

/**
 * Apply a rounding_rule record to a raw check_in / check_out pair.
 *
 * @param {string} checkIn   - ISO timestamp string
 * @param {string} checkOut  - ISO timestamp string
 * @param {object} rule      - row from rounding_rules table (may be null)
 * @returns {{ billableStart: Date, billableEnd: Date, billableMinutes: number, ruleName: string|null }}
 */
export function applyRoundingRules(checkIn, checkOut, rule) {
  const ciRaw = new Date(checkIn);
  const coRaw = new Date(checkOut);

  if (!rule) {
    const billableMinutes = Math.max(0, (coRaw - ciRaw) / 60000);
    return { billableStart: ciRaw, billableEnd: coRaw, billableMinutes, ruleName: null };
  }

  const shiftStartMin = timeStrToMinutes(rule.shift_start);
  const shiftEndMin   = timeStrToMinutes(rule.shift_end);
  const entryTol      = rule.entry_tolerance_min || 0;
  const exitTol       = rule.exit_tolerance_min  || 0;
  const interval      = rule.round_interval      || 15;
  const entryDir      = rule.entry_round_dir     || 'nearest';
  const exitDir       = rule.exit_round_dir      || 'nearest';

  // Minutes past midnight for raw times
  const ciMin = ciRaw.getHours() * 60 + ciRaw.getMinutes() + ciRaw.getSeconds() / 60;
  const coMin = coRaw.getHours() * 60 + coRaw.getMinutes() + coRaw.getSeconds() / 60;

  // ── Billable start ──────────────────────────────────────────────
  let billableStartMin;

  if (shiftStartMin !== null) {
    const diffFromStart = ciMin - shiftStartMin; // positive = arrived late

    if (Math.abs(diffFromStart) <= entryTol) {
      // Within tolerance → count from shift start
      billableStartMin = shiftStartMin;
    } else if (diffFromStart < 0) {
      // Arrived early
      billableStartMin = rule.pay_early_entry
        ? roundTime(ciMin, interval, entryDir)
        : shiftStartMin;
    } else {
      // Arrived late (beyond tolerance) → round the actual arrival
      billableStartMin = roundTime(ciMin, interval, entryDir);
    }
  } else {
    billableStartMin = roundTime(ciMin, interval, entryDir);
  }

  // ── Billable end ────────────────────────────────────────────────
  let billableEndMin;

  if (shiftEndMin !== null) {
    const diffFromEnd = coMin - shiftEndMin; // positive = stayed late

    if (Math.abs(diffFromEnd) <= exitTol) {
      // Within tolerance → count to shift end
      billableEndMin = shiftEndMin;
    } else if (diffFromEnd > 0) {
      // Left late
      billableEndMin = rule.pay_late_exit
        ? roundTime(coMin, interval, exitDir)
        : shiftEndMin;
    } else {
      // Left early (beyond tolerance) → round the actual departure
      billableEndMin = roundTime(coMin, interval, exitDir);
    }
  } else {
    billableEndMin = roundTime(coMin, interval, exitDir);
  }

  const billableStart   = setTimeOnDate(ciRaw, billableStartMin);
  const billableEnd     = setTimeOnDate(coRaw, billableEndMin);
  const billableMinutes = Math.max(0, (billableEnd - billableStart) / 60000);

  return { billableStart, billableEnd, billableMinutes, ruleName: rule.name };
}

/**
 * Find the best matching active rule for a given check_in time.
 * Falls back to the first active rule if no shift time matches, or null if none.
 */
export function findMatchingRule(checkIn, rules) {
  if (!rules || rules.length === 0) return null;
  const active = rules.filter(r => r.is_active);
  if (active.length === 0) return null;

  const ciMin = new Date(checkIn).getHours() * 60 + new Date(checkIn).getMinutes();

  const match = active.find(r => {
    const start = timeStrToMinutes(r.shift_start);
    const end   = timeStrToMinutes(r.shift_end);
    if (start === null || end === null) return false;
    const tol = r.entry_tolerance_min || 0;
    return ciMin >= start - tol && ciMin <= end + (r.exit_tolerance_min || 0);
  });

  return match || active[0];
}
